import React, { useState } from 'react'
import { useForm } from 'react-hook-form';
import { Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// layout 
import AuthLayout from '../../../layouts/AuthLayout/AuthLayout';

// components 
import { NioCard, NioBrand, NioField, NioButton, NioToaster } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import api from '../../../utils/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    mode: 'onBlur',
    defaultValues: { password: '', confirmPassword: '' },
  });
  const passwordValue = watch('password');

  const onSubmit = async ({ password }) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const response = await api.resetPassword(token, password);
      if (!response?.success) {
        throw new Error(response?.message || t('auth.resetFailed'));
      }
      setShowSuccess(true);
      setTimeout(() => navigate('/auth/login'), 2500);
    } catch (error) {
      setSubmitError(error.message || t('auth.resetFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title={t('auth.resetTitle')} rootClass="layout-1">
      {showSuccess && (
        <NioToaster
          variant="success"
          messages={[{ id: 1, header: t('auth.resetTitle'), body: t('auth.resetSuccess') }]}
        />
      )}
      <NioCard className="nk-form-card card-gutter-md nk-auth-form-card mx-md-9 mx-xl-auto" >
        <NioCard.Body>
          <div className="nk-form-card-head text-center pb-5">
            <div className="form-logo mb-2">
              <NioBrand logo="logo1" variant="dark" imageRoot="../images/" />
            </div>
            <h3 className="title mb-2">{t('auth.resetHeading')}</h3>
            <p className="text">{t('auth.forgotSub')} <Link to="/auth/login" className="btn-link text-primary">{t('nav.login')}</Link>.</p>
          </div>
          {!token ? (
            <p className="text-danger text-center mb-0">{t('auth.resetInvalidLink')}</p>
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="gy-4">
                <Col xs={12}>
                  <NioField htmlFor="password" label={t('auth.password')}>
                    <NioField.Input
                      id="password"
                      type="password"
                      icon="eye-fill after text-primary"
                      placeholder={t('auth.passwordPlaceholder')}
                      errors={errors.password}
                      {...register('password', {
                        required: t('auth.errors.passwordRequired'),
                        minLength: { value: 8, message: t('auth.pwdLength') },
                      })}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <NioField htmlFor="cpassword" label={t('auth.confirmPasswordLabel')}>
                    <NioField.Input
                      id="cpassword"
                      type="password"
                      icon="eye-fill after text-primary"
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      errors={errors.confirmPassword}
                      {...register('confirmPassword', {
                        required: t('auth.errors.confirmPasswordRequired'),
                        validate: (value) => value === passwordValue || t('auth.errors.passwordMismatch'),
                      })}
                    />
                  </NioField>
                </Col>
                {submitError && (
                  <Col xs={12}>
                    <p className="text-danger small mb-0">{submitError}</p>
                  </Col>
                )}
                <Col xs={12}>
                  <div className="form-group">
                    <NioButton
                      type="submit"
                      label={submitting ? t('auth.resetSubmitting') : t('auth.resetButton')}
                      className="btn-block btn-primary"
                      disabled={submitting}
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </NioCard.Body>
      </NioCard>
    </AuthLayout >
  )
}
